import { groupBy, keys } from 'lodash'

/**
 * Get entities with the same key.
 *
 * @param selectedEntities - An array of selected entities.
 * @param additionalEntities - An array of additional entities.
 * @returns An array of entities with the same key.
 *
 * @example
 * ```typescript
 * const selectedEntities: Entity[] = [
 *  { id: '1', code: 'A' },
 * { id: '2', code: 'B' }
 * ]
 * const additionalEntities: Entity[] = [
 * { id: '3', code: 'A' },
 * { id: '4', code: 'B' },
 * { id: '5', code: 'C' }
 * ]
 *
 * const result = getEntitiesWithSameKey(selectedEntities, additionalEntities, 'code')
 *
 * console.log(result)
 * // Output: [
 * //   { id: '1', code: 'A' },
 * //   { id: '3', code: 'A' },
 * //   { id: '2', code: 'B' },
 * //   { id: '4', code: 'B' }
 * // ]
 *
 * ```
 */
export const getEntitiesWithSameKey = <T extends Record<string, TSFixMe>>(
  selectedEntities: T[],
  additionalEntities: T[],
  commonKey: string
): T[] => {
  let newEntities: T[] = []
  const entitiesGroupedByCommonKey = groupBy(selectedEntities, commonKey)
  const entitiesGroupedById = groupBy(selectedEntities, 'id')
  keys(entitiesGroupedByCommonKey).forEach((keyValue) => {
    const entitiesByCommonKey = entitiesGroupedByCommonKey[keyValue]
    const notAddedEntitiesByCommonKey =
      additionalEntities.filter((item) => {
        return !entitiesGroupedById[item.id] && item[commonKey] === keyValue
      }) || []
    newEntities = [...newEntities, ...entitiesByCommonKey, ...notAddedEntitiesByCommonKey]
  })
  return newEntities
}
